'use client';

import { useState } from 'react';
import { CalendarIcon, CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { type DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  DATE_PICKER_MAX_RANGE_DAYS,
  PRESET_OPTIONS,
  computePeriodDateRange,
  type PeriodPreset,
  type PeriodSelection,
} from '@/lib/period-presets';
import { track } from '@/lib/telemetry';
import type { PeriodSurface } from '@/lib/telemetry/events';
import { cn } from '@/lib/utils';
import { spaceTelemetryTarget } from '@/lib/spaces/navigation';

export type { PeriodPreset, PeriodSelection } from '@/lib/period-presets';

interface PeriodPickerProps {
  value: PeriodSelection;
  onChange: (selection: PeriodSelection) => void;
  /** Popover alignment on desktop. Defaults to `end`. */
  align?: 'start' | 'end';
  /**
   * Telemetry surface for `ui.period_changed`. Defaults to `'global'` (the header
   * instance); the relocated transactions-filter instance passes `'transactions'`.
   */
  surface?: PeriodSurface;
  /** Keep room for the mobile scope switcher. */
  compact?: boolean;
}

// The `page` event property must stay a closed, ID-free set — normalize the
// dynamic route segments (cuids/route params) to their pattern.
function normalizedPage(pathname: string): string {
  return spaceTelemetryTarget(pathname)
    .replace(/^\/transactions\/category\/[^/]+/, '/transactions/category/:id')
    .replace(/^\/investments\/(?!calendar$)[^/]+$/, '/investments/:id')
    .replace(/^\/metas\/[^/]+\/edit$/, '/metas/:id/edit');
}

function formatTriggerLabel(value: PeriodSelection): string {
  if (value.preset === 'custom') {
    const fromFormat = value.from.getFullYear() === value.to.getFullYear() ? 'dd MMM' : 'dd MMM yyyy';
    const f = format(value.from, fromFormat, { locale: ptBR });
    const t = format(value.to, 'dd MMM yyyy', { locale: ptBR });
    return `${f} – ${t}`;
  }
  return PRESET_OPTIONS.find((o) => o.value === value.preset)?.label ?? 'Período';
}

export function PeriodPicker({ value, onChange, align = 'end', surface = 'global', compact = false }: PeriodPickerProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [stagedRange, setStagedRange] = useState<DateRange | undefined>(
    value.preset === 'custom' ? { from: value.from, to: value.to } : undefined,
  );
  const [customExpanded, setCustomExpanded] = useState(value.preset === 'custom');

  // Reset staged state on open transitions so re-opens start fresh from the
  // committed value (no leftover canceled selections).
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setStagedRange(value.preset === 'custom' ? { from: value.from, to: value.to } : undefined);
      setCustomExpanded(value.preset === 'custom');
    }
    setOpen(next);
  };

  const label = formatTriggerLabel(value);
  const applyPreset = (preset: PeriodPreset) => {
    if (preset === 'custom') {
      setCustomExpanded(true);
      return;
    }
    setOpen(false);
    // Re-selecting the active preset is a no-op — don't inflate the insight.
    if (preset !== value.preset) {
      track('ui.period_changed', {
        surface,
        method: 'preset',
        preset,
        page: normalizedPage(window.location.pathname),
      });
    }
    onChange({ preset, ...computePeriodDateRange(preset) });
  };

  const applyCustom = () => {
    if (!stagedRange?.from || !stagedRange?.to) return;
    // Applying the exact range already in effect is a no-op — don't inflate.
    const unchanged =
      value.preset === 'custom' &&
      format(stagedRange.from, 'yyyy-MM-dd') === format(value.from, 'yyyy-MM-dd') &&
      format(stagedRange.to, 'yyyy-MM-dd') === format(value.to, 'yyyy-MM-dd');
    if (!unchanged) {
      track('ui.period_changed', {
        surface,
        method: 'custom_range',
        preset: 'custom',
        page: normalizedPage(window.location.pathname),
      });
    }
    onChange({
      preset: 'custom',
      ...computePeriodDateRange(
        'custom',
        format(stagedRange.from, 'yyyy-MM-dd'),
        format(stagedRange.to, 'yyyy-MM-dd'),
      ),
    });
    setOpen(false);
  };

  const cancel = () => {
    setOpen(false);
  };

  // Single trigger button — same look on both viewports.
  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={`Período: ${label}`}
      className={cn('inline-flex h-9 items-center gap-2 rounded-xl bg-card font-medium', compact && 'w-9 px-0 sm:w-auto sm:px-3')}
    >
      <CalendarIcon className="size-4 shrink-0 text-text-secondary" weight="bold" />
      <span className={cn('max-w-[14ch] truncate sm:max-w-none', compact && 'hidden sm:inline')}>{label}</span>
      <CaretDownIcon className={cn('size-3 shrink-0 text-text-tertiary', compact && 'hidden sm:inline')} weight="bold" />
    </Button>
  );

  // Shared preset row — used inside both Sheet (mobile) and Popover (desktop).
  const presetList = (
    <div role="list" className="flex flex-col gap-0.5">
      {PRESET_OPTIONS.map((opt) => {
        const isCustom = opt.value === 'custom';
        const active =
          (isCustom && (value.preset === 'custom' || customExpanded)) ||
          (!isCustom && value.preset === opt.value);

        return (
          <button
            key={opt.value}
            type="button"
            role="listitem"
            onClick={() => applyPreset(opt.value)}
            className={cn(
              'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
              active
                ? 'bg-success-surface text-success-text'
                : 'text-text-secondary hover:bg-surface-sunken active:bg-muted',
            )}
          >
            <span>{opt.label}</span>
            {active && <CheckIcon className="size-4 text-success-text" weight="bold" />}
          </button>
        );
      })}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl px-4 pt-4 pb-[max(2rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="mb-2 text-left">
            <SheetTitle className="text-sm font-bold text-text-secondary">Período</SheetTitle>
            <SheetDescription className="sr-only">
              Selecione um período predefinido ou personalize um intervalo de datas.
            </SheetDescription>
          </SheetHeader>

          {presetList}

          {customExpanded && (
            <div className="mt-3 border-t border-border pt-3">
              <Calendar
                mode="range"
                selected={stagedRange}
                onSelect={setStagedRange}
                numberOfMonths={1}
                max={DATE_PICKER_MAX_RANGE_DAYS}
                excludeDisabled
                disableFutureDates
                locale={ptBR}
                className="mx-auto"
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={applyCustom}
                  disabled={!stagedRange?.from || !stagedRange?.to}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto overflow-hidden rounded-xl p-0"
        sideOffset={6}
      >
        <div className="flex">
          <div className="w-44 border-r border-border p-2">{presetList}</div>
          <div className="flex flex-col">
            <div className="p-3">
              <Calendar
                mode="range"
                selected={stagedRange}
                onSelect={(r) => {
                  setStagedRange(r);
                  setCustomExpanded(true);
                }}
                numberOfMonths={2}
                max={DATE_PICKER_MAX_RANGE_DAYS}
                excludeDisabled
                disableFutureDates
                locale={ptBR}
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
              <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={applyCustom}
                disabled={!stagedRange?.from || !stagedRange?.to}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
